"use client";

import Link from "@/components/ui/CcLink";
import { usePathname } from "next/navigation";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import { CcNavInlineDetailLabel } from "@/components/layout/CcNavInlineDetailLabel";
import { CcMegaAllLink } from "@/components/ui/CcMegaAllLink";
import type {
  NavResourceMegaColumn,
  NavResourcesMegaCopy,
} from "@/lib/cc-nav-resources-mega";
import type { NavResourceItem } from "@/lib/cc-nav-resources";
import {
  useCcNavMegaMountEl,
  useCcNavMegaPanelPosition,
} from "@/lib/cc-nav-mega-panel-position";

const OPEN_DELAY_MS = 100;
const CLOSE_DELAY_MS = 240;
const HOVER_INTENT_MQ =
  "(hover: hover) and (pointer: fine) and (min-width: 811px)";

type CcNavResourcesMenuProps = {
  label: string;
  href: string;
  isActive: boolean;
  resourceItems: NavResourceItem[];
  resourceMegaColumns: NavResourceMegaColumn[];
  megaCopy: NavResourcesMegaCopy;
  onNavigate: () => void;
  detailTitle?: string | null;
  detailLead?: string | null;
  detailLeadHref?: string | null;
  detailTitleFallback?: string | null;
  detailInitiallySettled?: boolean;
  onDetailRailActiveChange?: (active: boolean) => void;
  megaMountRef?: RefObject<HTMLElement | null>;
};

export function CcNavResourcesMenu({
  label,
  href,
  isActive,
  resourceItems,
  resourceMegaColumns,
  megaCopy,
  onNavigate,
  detailTitle = null,
  detailLead = null,
  detailLeadHref = null,
  detailTitleFallback = null,
  detailInitiallySettled = false,
  onDetailRailActiveChange,
  megaMountRef,
}: CcNavResourcesMenuProps) {
  const [detailRailActive, setDetailRailActive] = useState(false);
  const showDetailLayout =
    Boolean(detailTitle) ||
    Boolean(detailTitleFallback) ||
    detailRailActive;
  const detailAria = detailTitle ?? detailTitleFallback ?? null;
  const detailAriaLabel =
    detailAria && detailLead
      ? `${detailLead} / ${detailAria}`
      : detailAria;
  const hasSegmentedTrail =
    Boolean(detailLead?.trim()) &&
    Boolean(detailLeadHref?.trim()) &&
    Boolean(detailAria?.trim());

  const handleDetailRailChange = useCallback(
    (active: boolean) => {
      setDetailRailActive(active);
      onDetailRailActiveChange?.(active);
    },
    [onDetailRailActiveChange],
  );

  const pathname = usePathname();
  const megaLinks = resourceMegaColumns.flatMap((column) => column.links);
  const activeDetailIndex = megaLinks.findIndex(
    (item) =>
      pathname === item.href || pathname.startsWith(`${item.href}/`),
  );
  const legacyActiveDetailIndex = resourceItems.findIndex(
    (item) =>
      pathname === item.href || pathname.startsWith(`${item.href}/`),
  );
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerSpanRef = useRef<HTMLSpanElement>(null);
  const focusTrigger = () => {
    triggerSpanRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
  };
  const panelRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const focusFirstRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [hoverIntent, setHoverIntent] = useState(false);
  const megaMountEl = useCcNavMegaMountEl(megaMountRef);
  const pillMounted = Boolean(megaMountEl);
  const alignEnd = useCcNavMegaPanelPosition(
    open,
    megaMountEl,
    triggerSpanRef,
    panelRef,
    [resourceMegaColumns.length],
    true,
  );

  const clearTimers = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearTimers();
    setOpen(true);
  }, [clearTimers]);

  const closeNow = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [clearTimers]);

  useLayoutEffect(() => {
    const mq = window.matchMedia(HOVER_INTENT_MQ);
    const sync = () => setHoverIntent(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    closeNow();
  }, [pathname, closeNow]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!open || !focusFirstRef.current) return;
    focusFirstRef.current = false;
    firstItemRef.current?.focus();
  }, [open]);

  const scheduleOpen = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (open) return;
    if (openTimerRef.current !== null) return;
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (!open) return;
    if (closeTimerRef.current !== null) return;
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const handlePointerEnter = () => {
    if (!hoverIntent) return;
    scheduleOpen();
  };

  const handlePointerLeave = () => {
    if (!hoverIntent) return;
    scheduleClose();
  };

  const handleTriggerClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      hoverIntent || event.metaKey || event.ctrlKey || event.shiftKey ||
      event.altKey || event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    setOpen((current) => !current);
  };

  const handleTriggerKeyDown = (
    event: KeyboardEvent<HTMLAnchorElement | HTMLSpanElement>,
  ) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (open) {
        firstItemRef.current?.focus();
        return;
      }
      focusFirstRef.current = true;
      openNow();
    }

    if (event.key === "Escape" && open) {
      event.preventDefault();
      closeNow();
      focusTrigger();
    }
  };

  const handleRootKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || !open) return;
    event.preventDefault();
    closeNow();
    focusTrigger();
  };

  const handleRootBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && rootRef.current?.contains(next)) return;
    if (next instanceof Node && panelRef.current?.contains(next)) return;
    closeNow();
  };

  const handlePanelPointerEnter = () => {
    if (!hoverIntent || !pillMounted) return;
    scheduleOpen();
  };

  const handlePanelPointerLeave = () => {
    if (!hoverIntent || !pillMounted) return;
    scheduleClose();
  };

  const handleItemNavigate = () => {
    closeNow();
    onNavigate();
  };

  let firstLinkAssigned = false;
  const megaColumnCount = Math.min(
    5,
    Math.max(1, resourceMegaColumns.length),
  );

  const panel = (
      <div
        ref={panelRef}
        id={panelId}
        className={`cc-nav-mega cc-nav-resources-panel ${pillMounted ? "cc-nav-mega--pill-mounted" : ""} ${open ? "cc-nav-mega--open" : ""}`}
        style={
          {
            "--cc-resources-mega-columns": megaColumnCount,
          } as CSSProperties
        }
        inert={!open}
        aria-hidden={!open}
        onPointerEnter={handlePanelPointerEnter}
        onPointerLeave={handlePanelPointerLeave}
      >
        <div className="cc-nav-mega__chrome" data-cc-cursor-ignore aria-hidden="true" />
        <div className="cc-nav-resources-panel__grid">
          {resourceMegaColumns.map((column) => (
            <section
              key={column.id}
              className="cc-nav-resources-panel__column"
              aria-labelledby={`${panelId}-${column.id}-head`}
            >
              <CcMegaAllLink
                id={`${panelId}-${column.id}-head`}
                href={column.headerHref}
                className="cc-nav-resources-panel__column-head"
                onNavigate={handleItemNavigate}
              >
                {column.header}
              </CcMegaAllLink>
              <ul className="cc-nav-resources-panel__list">
                {column.links.map((link) => {
                  const itemActive =
                    pathname === link.href ||
                    pathname.startsWith(`${link.href}/`);
                  const assignFirstRef = !firstLinkAssigned;
                  if (assignFirstRef) {
                    firstLinkAssigned = true;
                  }
                  const itemClassName = [
                    "cc-nav-mega__item",
                    itemActive ? "cc-nav-mega__item--active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <li key={link.key} className="cc-nav-mega__row">
                      <Link
                        ref={assignFirstRef ? firstItemRef : undefined}
                        href={link.href}
                        className={itemClassName}
                        onNavigate={handleItemNavigate}
                      >
                        <span className="cc-nav-mega__item-label">
                          {link.label}
                        </span>
                        <span className="cc-nav-mega__item-desc">
                          {link.description}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
        <div className="cc-nav-mega__rule" data-cc-cursor-ignore />
        <CcMegaAllLink href={href} onNavigate={handleItemNavigate}>
          {megaCopy.seeAll}
        </CcMegaAllLink>
      </div>
  );

  return (
    <div
      ref={rootRef}
      className={`cc-nav-resources ${open ? "cc-nav-resources--open" : ""} ${!pillMounted && alignEnd ? "cc-nav-resources--align-end" : ""}`}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onKeyDown={handleRootKeyDown}
      onBlur={handleRootBlur}
    >
      {/* Keep the animation component mounted as the trail gains/loses segments. */}
      <span
        ref={triggerSpanRef}
        className={`cc-nav__link cc-nav-resources__trigger ${isActive ? "cc-nav__link--active" : ""} ${showDetailLayout ? "cc-nav__link--has-detail" : ""} ${hasSegmentedTrail ? "cc-nav-resources__trigger--segmented" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={
          detailAriaLabel ? `${label} / ${detailAriaLabel}` : undefined
        }
        onKeyDown={handleTriggerKeyDown}
      >
        <CcNavInlineDetailLabel
          label={label}
          labelHref={href}
          onLabelClick={hasSegmentedTrail ? undefined : handleTriggerClick}
          detail={detailTitle}
          detailLead={detailLead}
          detailLeadHref={detailLeadHref}
          onSegmentNavigate={onNavigate}
          detailIndex={
            activeDetailIndex >= 0
              ? activeDetailIndex
              : legacyActiveDetailIndex >= 0
                ? legacyActiveDetailIndex
                : null
          }
          detailFallback={detailTitleFallback}
          detailInitiallySettled={detailInitiallySettled}
          onDetailRailActiveChange={handleDetailRailChange}
        />
      </span>

      {open ? (
        <span className="cc-nav-resources__bridge" aria-hidden="true" />
      ) : null}

      {pillMounted && megaMountEl
        ? createPortal(panel, megaMountEl)
        : panel}
    </div>
  );
}
